import React from "react";
import PropTypes from "prop-types";
import { inspectorFor } from "./blockInspector";
import { METRIC_CATALOG } from "../metrics/reportMetrics";
import useBlockData from "../blocks/useBlockData";
import BulletsField from "./fields/BulletsField";
import TextField from "./fields/TextField";
import SummaryField from "./fields/SummaryField";
import PlainField from "./fields/PlainField";
import DriverAnnotations from "./fields/DriverAnnotations";
import DriverOverview from "./fields/DriverOverview";
import FigureDataPanel, { hasFigurePanel } from "./figureDataPanels";

// humanizeNarrativeKey - "what_changed" -> "What changed", "decision_owner" -> "Decision owner".
// Deliberately produces the SAME strings NarrativeRail's static labels already used (see
// NarrativeRail.test.jsx / the field components) so a field looks identical whether it's reached
// through NarrativeRail or through this contextual inspector.
function humanizeNarrativeKey(key) {
  if (!key) return "";
  return key
    .split("_")
    .filter(Boolean)
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

// envelopeKeyFor - "figures/forecast-band" -> "forecast_band" (the writeJSON envelope key the Go
// handler wraps its payload under - see useReportMetrics.js's ENDPOINT_ENVELOPE for the endpoints
// that already have a confirmed mapping; this generalizes the same "last segment, dashes ->
// underscores" convention to the endpoints that aren't in METRIC_CATALOG yet).
function envelopeKeyFor(dataEndpoint) {
  if (!dataEndpoint) return "";
  const last = dataEndpoint.split("/").pop() || "";
  return last.replace(/-/g, "_");
}

// formatRawValue - a generic, defensive display for one top-level field of a figure response
// whose shape isn't in METRIC_CATALOG: arrays show a count, nested objects a placeholder, and
// scalars render directly. Never throws.
function formatRawValue(value) {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (typeof value === "object") return "…";
  return String(value);
}

/**
 * SourceDataPanel - the read-only "Source data" list an edit-kind inspector shows below its
 * fields: each of `sourceMetricKeys` rendered as `label: value` from the already-resolved
 * `metrics` map (see reportMetrics.js's resolveMetrics) - no fetching of its own. A key missing
 * from `metrics` (not yet resolved, or the caller didn't pass one) falls back to the raw key as
 * its own label and an em-dash value, so a partially-loaded `metrics` never blanks the panel.
 */
const SourceDataPanel = ({ sourceMetricKeys, metrics }) => {
  if (!sourceMetricKeys || sourceMetricKeys.length === 0) return null;
  return (
    <div className="mt-5 rounded-card border border-card-bd bg-background-grey p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-text-3 mb-3">
        Source data · what this is about
      </h4>
      <dl className="divide-y divide-border">
        {sourceMetricKeys.map((key) => {
          const metric = metrics && metrics[key];
          return (
            <div key={key} className="flex items-center justify-between py-2 text-xs first:pt-0 last:pb-0">
              <dt className="text-text-2">{(metric && metric.label) || key}</dt>
              <dd className="font-semibold text-text tabular-nums">{(metric && metric.value) ?? "—"}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};

SourceDataPanel.propTypes = {
  sourceMetricKeys: PropTypes.arrayOf(PropTypes.string),
  metrics: PropTypes.object,
};

/**
 * GenericFigureList - the read-only fallback for a chart/table block's figure endpoint that has
 * NO METRIC_CATALOG coverage (e.g. figures/trend, figures/horizon, figures/data-quality,
 * figures/detailed-cost, figures/risk-bridge): lists up to 8 top-level key/value pairs from the
 * unwrapped figure object, so the inspector still shows SOMETHING for a block type this module
 * doesn't have a bespoke rendering for, rather than a blank panel.
 */
const GenericFigureList = ({ figure }) => {
  if (!figure || typeof figure !== "object") {
    return <p className="text-xs text-text-3">No data.</p>;
  }
  const entries = Object.entries(figure).slice(0, 8);
  if (entries.length === 0) return <p className="text-xs text-text-3">No data.</p>;
  return (
    <dl className="space-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between text-xs">
          <dt className="text-text-2">{key}</dt>
          <dd className="font-medium text-text">{formatRawValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

GenericFigureList.propTypes = {
  figure: PropTypes.object,
};

/**
 * ChartDataPanel - the read-only "Data behind this" body for a kind:'data' block. Always mounted
 * (only ever mounted BY THE PARENT when `dataEndpoint` is truthy - a null dataEndpoint renders a
 * plain note instead, without mounting this at all), so useBlockData here is called
 * unconditionally within this component instance - satisfying the Rules of Hooks without
 * BoardReportInspector having to call the hook conditionally itself.
 *
 * Reuses the already-resolved `metrics` map where METRIC_CATALOG has coverage for this endpoint
 * (kpi_strip/performance_table/forecast_band/variance_waterfall today); otherwise falls back to
 * GenericFigureList over the freshly-fetched raw figure.
 */
const ChartDataPanel = ({ boardReportId, runId, dataEndpoint, metrics }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/${dataEndpoint}`
  );

  if (isLoading) return <p className="text-xs text-text-3">Loading…</p>;
  if (error) return <p className="text-xs text-danger">Could not load this block&apos;s data.</p>;

  const envelopeKey = envelopeKeyFor(dataEndpoint);
  const figure = data ? data[envelopeKey] : undefined;

  // 1) Bespoke, formatted panel for this endpoint (trend/pvm/horizon/risk-bridge/data-quality/
  //    detailed-cost - figureDataPanels.jsx). Takes precedence over the catalog panel so
  //    figures/pvm shows the FULL per-driver breakdown, not just the 2 top-driver catalog metrics.
  if (hasFigurePanel(dataEndpoint)) {
    return <FigureDataPanel endpoint={dataEndpoint} figure={figure} />;
  }

  // 2) Catalog-backed source panel for endpoints with METRIC_CATALOG coverage (kpis / performance /
  //    forecast-band) - already readable, left unchanged.
  const catalogKeys = METRIC_CATALOG.filter((m) => m.endpoint === dataEndpoint).map((m) => m.key);
  if (catalogKeys.length > 0 && metrics) {
    return <SourceDataPanel sourceMetricKeys={catalogKeys} metrics={metrics} />;
  }

  // 3) Last-resort generic key/value list for an endpoint with neither a bespoke nor catalog
  //    rendering yet.
  return <GenericFigureList figure={figure} />;
};

ChartDataPanel.propTypes = {
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataEndpoint: PropTypes.string.isRequired,
  metrics: PropTypes.object,
};

// renderField - dispatches one inspectorFor() field descriptor to its field component, wired to
// the container's on* callbacks. A plain function (not a component) since each branch just
// returns a differently-typed element - there's no local state or lifecycle of its own here.
function renderField(field, ctx) {
  const {
    narrative,
    drivers,
    isReadOnly,
    metrics,
    catalog,
    onNarrativeField,
    onBulletChange,
    onAddBullet,
    onRemoveBullet,
    onDriverFieldChange,
    onFieldFocus,
  } = ctx;

  const focusField = (key) => () => {
    if (onFieldFocus) onFieldFocus(key);
  };

  switch (field.type) {
    case "bullets":
      return (
        <BulletsField
          key="bullets"
          bullets={narrative.bullets || []}
          disabled={isReadOnly}
          onBulletChange={onBulletChange}
          onAddBullet={onAddBullet}
          onRemoveBullet={onRemoveBullet}
          onFocus={onFieldFocus}
        />
      );
    case "summary":
      return (
        <SummaryField
          key={field.narrativeKey || "summary"}
          label={humanizeNarrativeKey(field.narrativeKey)}
          value={field.narrativeKey ? narrative[field.narrativeKey] : ""}
          onChange={(v) => onNarrativeField(field.narrativeKey, v)}
          disabled={isReadOnly}
          metrics={metrics}
          catalog={catalog}
          onFocus={focusField(field.narrativeKey)}
        />
      );
    case "text":
      return (
        <TextField
          key={field.narrativeKey}
          label={humanizeNarrativeKey(field.narrativeKey)}
          value={narrative[field.narrativeKey]}
          onChange={(v) => onNarrativeField(field.narrativeKey, v)}
          disabled={isReadOnly}
          metrics={metrics}
          catalog={catalog}
          onFocus={focusField(field.narrativeKey)}
        />
      );
    case "plain":
      return (
        <PlainField
          key={field.narrativeKey}
          label={humanizeNarrativeKey(field.narrativeKey)}
          value={narrative[field.narrativeKey]}
          onChange={(v) => onNarrativeField(field.narrativeKey, v)}
          disabled={isReadOnly}
          onFocus={focusField(field.narrativeKey)}
        />
      );
    case "drivers":
      return (
        <DriverAnnotations
          key="drivers"
          drivers={drivers || []}
          onlyIndex={field.onlyIndex}
          disabled={isReadOnly}
          onDriverFieldChange={onDriverFieldChange}
          onFocus={onFieldFocus}
        />
      );
    default:
      return null;
  }
}

/**
 * InspectorHeader - the compact card header: a status dot + the block title on the left, and a
 * muted "kind" label on the right ("Narrative" for an editable block, "Data" for a chart/table
 * block) — matching the mockup's "● Executive summary            Narrative" treatment, so a
 * reviewer always knows which block they're editing and whether it's editable or read-only.
 */
const InspectorHeader = ({ title, kind }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
    <h3 className="text-sm font-semibold text-text truncate">{title}</h3>
    <span className="ml-auto text-xs text-text-3">{kind}</span>
  </div>
);

InspectorHeader.propTypes = {
  title: PropTypes.string,
  kind: PropTypes.string,
};

/**
 * BoardReportInspector - the contextual, per-selected-block editor (SP6 Task 5). Given whichever
 * block is currently selected on the report canvas (`selectedBlockId`/`selectedBlockType`/
 * `selectedBinding` - see ManifestPage.jsx's selection seam), resolves `inspectorFor(...)`
 * (blockInspector.js) and renders either:
 *   - kind:'edit' - the mapped field editors (reusing the SAME field components NarrativeRail
 *     uses, ./fields/*), wired to the container's on* callbacks, plus a read-only "Source data"
 *     panel of the metrics backing this block.
 *   - kind:'data' - a read-only "Data behind this" panel (ChartDataPanel/GenericFigureList) for a
 *     chart/table block; a null dataEndpoint (unrecognized block type) shows a graceful note
 *     instead of fetching anything.
 * Nothing selected -> an empty-state prompt.
 */
const BoardReportInspector = ({
  selectedBlockId,
  selectedBlockType,
  selectedBinding,
  narrative,
  drivers,
  metrics,
  catalog,
  isReadOnly,
  onNarrativeField,
  onBulletChange,
  onAddBullet,
  onRemoveBullet,
  onDriverFieldChange,
  onFieldFocus,
  onSelectBlock,
  boardReportId,
  runId,
}) => {
  if (!selectedBlockId) {
    return (
      <div className="bg-card border border-card-bd rounded-card p-5">
        <p className="text-xs text-text-3">Select a block in the report to edit it.</p>
      </div>
    );
  }

  const inspector = inspectorFor(selectedBlockType, selectedBinding);

  // selectDriver / clearDriver drive the driver_table two-level flow: the overview drills into one
  // driver (adds driverIndex to the binding, exactly like a canvas row click), and the single-driver
  // view's "All drivers" back link returns to the overview (drops driverIndex). Both reuse the same
  // onSelectBlock the report canvas uses, so selection stays single-sourced.
  const selectDriver = (driverIndex) =>
    onSelectBlock && onSelectBlock(selectedBlockId, selectedBlockType, { ...(selectedBinding || {}), driverIndex });
  const clearDriver = () => {
    if (!onSelectBlock) return;
    const rest = { ...(selectedBinding || {}) };
    delete rest.driverIndex;
    onSelectBlock(selectedBlockId, selectedBlockType, rest);
  };

  if (inspector.kind === "driverOverview") {
    return (
      <div className="bg-card border border-card-bd rounded-card p-5 shadow-sm">
        <InspectorHeader title={inspector.title} kind="Drivers" />
        <p className="text-xs text-text-3 mb-3">Select a driver to add or edit its annotation.</p>
        <DriverOverview drivers={drivers || []} onSelect={selectDriver} />
      </div>
    );
  }

  if (inspector.kind === "edit") {
    const ctx = {
      narrative: narrative || {},
      drivers: drivers || [],
      isReadOnly,
      metrics,
      catalog,
      onNarrativeField,
      onBulletChange,
      onAddBullet,
      onRemoveBullet,
      onDriverFieldChange,
      onFieldFocus,
    };
    // A single-driver annotation view (driver_table drilled in) gets a back link to the overview.
    const driverField = inspector.fields.find((f) => f.type === "drivers");
    const isSingleDriver = driverField && driverField.onlyIndex != null;
    return (
      <div className="bg-card border border-card-bd rounded-card p-5 shadow-sm">
        {isSingleDriver && (
          <button
            type="button"
            onClick={clearDriver}
            className="inline-flex items-center gap-1 text-xs text-text-3 hover:text-text mb-3 cursor-pointer"
          >
            <span aria-hidden="true">←</span> All drivers
          </button>
        )}
        <InspectorHeader title={inspector.title} kind="Narrative" />
        <div className="space-y-4">{inspector.fields.map((field) => renderField(field, ctx))}</div>
        <SourceDataPanel sourceMetricKeys={inspector.sourceMetricKeys} metrics={metrics} />
      </div>
    );
  }

  if (inspector.kind === "note") {
    // notes -> the run's editable free text (binding.text); appendix_notes / notes_confidence ->
    // a documented staticText describing the block, since neither carries per-run binding text.
    const noteText = inspector.staticText || (selectedBinding && selectedBinding.text);
    return (
      <div className="bg-card border border-card-bd rounded-card p-5 shadow-sm">
        <InspectorHeader title={inspector.title} kind="Notes" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-text-3 mb-2">
          Note content
        </h4>
        {noteText ? (
          <p className="text-xs text-text-2 whitespace-pre-wrap">{noteText}</p>
        ) : (
          <p className="text-xs text-text-3">No note text for this block.</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card border border-card-bd rounded-card p-5 shadow-sm">
      <InspectorHeader title={inspector.title} kind="Data" />
      <h4 className="text-xs font-semibold uppercase tracking-wide text-text-3 mb-2">
        Data behind this
      </h4>
      {inspector.dataEndpoint ? (
        <ChartDataPanel
          boardReportId={boardReportId}
          runId={runId}
          dataEndpoint={inspector.dataEndpoint}
          metrics={metrics}
        />
      ) : (
        <p className="text-xs text-text-3">No data available for this block.</p>
      )}
    </div>
  );
};

BoardReportInspector.propTypes = {
  selectedBlockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedBlockType: PropTypes.string,
  selectedBinding: PropTypes.object,
  narrative: PropTypes.object,
  drivers: PropTypes.array,
  metrics: PropTypes.object,
  catalog: PropTypes.array,
  isReadOnly: PropTypes.bool,
  onNarrativeField: PropTypes.func,
  onBulletChange: PropTypes.func,
  onAddBullet: PropTypes.func,
  onRemoveBullet: PropTypes.func,
  onDriverFieldChange: PropTypes.func,
  onFieldFocus: PropTypes.func,
  // The same block-selection callback the report canvas uses; lets the driver overview drill into a
  // single driver and the single-driver view return to the overview.
  onSelectBlock: PropTypes.func,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BoardReportInspector;
